import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Sidebar,
  CharacterCard,
  UserProfileHeader,
  MobileNav,
  CustomButton,
  Pagination,
} from '@client/components'
import { getUserCreatedCharacters as useUserCreatedCharacters } from '@client/components/service'
import { useMemoryOptimizer } from '@client/utility'
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Grid,
} from '@mui/material'

export function ProfilePage() {
  const urlParams = useParams<{ creator_uuid?: string }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { optimize } = useMemoryOptimizer()
  
  const creatorUUID = urlParams.creator_uuid || null
  
  const {
    characters,
    userData,
    loading,
    maxPage,
    currentPage,
    handleButtonTagClicked,
    handleButtonClick,
    goToNextPage,
    goToPrevPage,
  } = useUserCreatedCharacters(creatorUUID, 20)

  const handleButtonClickForCustomButton = async (
    type: 'hot' | 'latest' | 'random' | 'tags',
    page: number,
    options?: { tag?: string; gender?: 'male' | 'female' }
  ) => {
    await handleButtonClick(type, 20, page, options?.tag, options?.gender)
  }

  useEffect(() => {
    if (characters.length > 0) optimize()
  }, [characters, optimize])

  useEffect(() => {
    const scrollContainer = document.getElementById('profile-scroll-container')
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentPage])

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <Sidebar />
      <Box display="flex" flex={1}>
        <Box id="profile-scroll-container" flex={1} overflow="auto" pb={isMobile ? 12 : 0}>
          {loading ? (
            <Box display="flex" justifyContent="center" pt={10}>
              <CircularProgress />
            </Box>
          ) : !userData ? (
            <Box display="flex" justifyContent="center" pt={10}>
              <Typography>This user does not exist (¬_¬ )</Typography>
            </Box>
          ) : (
            <>
              <UserProfileHeader user={userData} />
              <Box px={3} pt={2}>
                <CustomButton
                  onButtonClick={handleButtonClickForCustomButton}
                  onButtonTagClicked={handleButtonTagClicked}
                />
              </Box>
              <Grid
                container
                spacing={2}
                sx={{
                  pb: 6,
                  minHeight: '50vh',
                  pl: { md: 10 },
                }}
              >
                {characters.length > 0 ? (
                  characters.map((char) => (
                    <Grid
                      key={char.id}
                      size={{ xs: 6, md: 4, lg: 3, xl: 2.4, xxl: 2 }}
                    >
                      <CharacterCard
                        character={{ ...char, creator_uuid: creatorUUID ?? undefined }}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid size={12}>
                    <Typography
                      variant="h6"
                      sx={{ textAlign: 'center' }}
                    >
                      This user hasn't created anything yet. 💤
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Box mb={isMobile ? 10 : 4}>
                <Pagination
                  totalPages={maxPage}
                  currentPage={currentPage}
                  onNext={goToNextPage}
                  onPrev={goToPrevPage}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      {isMobile && <MobileNav setShowLoginModal={setShowLoginModal} />}
    </Box>
  )
}